import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthStatusScreen } from "@/components/auth/AuthStatusScreen";
import { useAuth } from "@/contexts/AuthContext";

export function VerifyRootInvitePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { verifyRootInviteAndLogin, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState<"pending" | "success" | "error" | "expired">(
    "pending",
  );
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      toast.error(t("auth.verify.error"));
      setStatus("error");
      return;
    }
    if (isLoading || isAuthenticated) return;
    if (startedRef.current) return;
    startedRef.current = true;

    verifyRootInviteAndLogin(token)
      .then(() => {
        if (mountedRef.current) {
          toast.success(t("auth.verify.successToast"));
          setStatus("success");
        }
      })
      .catch((err: { response?: { status?: number } }) => {
        if (!mountedRef.current) return;
        // Backend returns 401 for expired and 400 for already-used/invalid; both
        // mean "ask for a fresh invite" so we collapse them into one branch.
        const code = err?.response?.status;
        if (code === 401 || code === 400) {
          setStatus("expired");
        } else {
          toast.error(t("auth.verify.error"));
          setStatus("error");
        }
      });
  }, [token, verifyRootInviteAndLogin, isLoading, isAuthenticated, t]);

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/tenants" replace />;
  }

  if (status === "success") {
    return <Navigate to="/tenants" replace />;
  }

  if (status === "expired") {
    return (
      <AuthStatusScreen
        state="error"
        title={t("auth.verifyRootInvite.expiredTitle", "Invitación vencida")}
        message={t(
          "auth.verifyRootInvite.expired",
          "Este enlace de invitación expiró. Pedile al equipo que te reenvíe la invitación.",
        )}
        action={
          <Link to="/login" className="fx-auth-btn fx-auth-btn-primary">
            {t("pages.common.back")}
          </Link>
        }
      />
    );
  }

  if (status === "error") {
    return (
      <AuthStatusScreen
        state="error"
        title={t("auth.verify.errorTitle", "No pudimos verificar el enlace")}
        message={t("auth.verify.error")}
        action={
          <Link to="/login" className="fx-auth-btn fx-auth-btn-primary">
            {t("pages.common.back")}
          </Link>
        }
      />
    );
  }

  return (
    <AuthStatusScreen
      state="loading"
      title={t("auth.verify.pendingTitle", "Verificando tu enlace")}
      message={t(
        "auth.verify.pendingDetail",
        "Estamos confirmando tu acceso. Esto toma un segundo.",
      )}
    />
  );
}
